/**
 * Les champs du pivot, nature par nature, pour le mode `?demo` : une copie de
 * ce que sert `GET /v1/console/profils/cibles` (`apps/api/src/reprise/cibles.ts`,
 * exactement les colonnes des gabarits). À régénérer si un gabarit change.
 */
import type { CiblesPivot } from './correspondance.js';

export const CIBLES_DEMO: CiblesPivot = {
  natures: [
    {
      nature: 'lieux',
      colonnes: [
        {
          cle: 'nom',
          entete: 'Nom',
          aide: 'Obligatoire. Casse et espaces ignorés, accents gardés : « réserve » et « Réserve » sont le même lieu.',
          obligatoire: true,
        },
        {
          cle: 'type',
          entete: 'Type',
          aide: 'interne, externe, foire, client. Vide : interne.',
          obligatoire: false,
        },
        {
          cle: 'libelleAdresse',
          entete: 'Libellé de l’adresse',
          aide: 'Facultatif.',
          obligatoire: false,
        },
        { cle: 'adresse', entete: 'Adresse', aide: 'Numéro et voie.', obligatoire: false },
        {
          cle: 'complementAdresse',
          entete: 'Complément d’adresse',
          aide: 'Facultatif.',
          obligatoire: false,
        },
        { cle: 'codePostal', entete: 'Code postal', aide: 'Facultatif.', obligatoire: false },
        { cle: 'ville', entete: 'Ville', aide: 'Facultatif.', obligatoire: false },
        {
          cle: 'pays',
          entete: 'Pays',
          aide: 'Code ISO (FR, BE…) ou nom français. Vide : FR.',
          obligatoire: false,
        },
        {
          cle: 'notes',
          entete: 'Notes',
          aide: 'Facultatif, 2 000 caractères au plus.',
          obligatoire: false,
        },
        { cle: 'actif', entete: 'Actif', aide: 'oui ou non. Vide : oui.', obligatoire: false },
      ],
    },
    {
      nature: 'artistes',
      colonnes: [
        {
          cle: 'prenom',
          entete: 'Prénom',
          aide: 'Facultatif. Placé devant le nom : « Hélène » + « Sorel » donne « Hélène Sorel ».',
          obligatoire: false,
        },
        {
          cle: 'nom',
          entete: 'Nom',
          aide: 'Obligatoire. Nom d’usage de l’artiste tel qu’il s’affiche. Casse et espaces ignorés, accents gardés : « Ines » et « Inès » restent deux artistes (avertissement).',
          obligatoire: true,
        },
        {
          cle: 'naissance',
          entete: 'Naissance',
          aide: 'Texte libre, 120 caractères (« 1974 », « Brest, 1974 »).',
          obligatoire: false,
        },
        {
          cle: 'nationalite',
          entete: 'Nationalité',
          aide: 'Code pays ISO (FR, BE…) ou nom français du pays.',
          obligatoire: false,
        },
        { cle: 'biographieFr', entete: 'Biographie (FR)', aide: 'Facultatif.', obligatoire: false },
        { cle: 'biographieEn', entete: 'Biographie (EN)', aide: 'Facultatif.', obligatoire: false },
        { cle: 'cvFr', entete: 'CV (FR)', aide: 'Facultatif.', obligatoire: false },
        { cle: 'cvEn', entete: 'CV (EN)', aide: 'Facultatif.', obligatoire: false },
        { cle: 'presseFr', entete: 'Presse (FR)', aide: 'Facultatif.', obligatoire: false },
        { cle: 'presseEn', entete: 'Presse (EN)', aide: 'Facultatif.', obligatoire: false },
        {
          cle: 'representee',
          entete: 'Représentée',
          aide: 'oui ou non. Vide : non.',
          obligatoire: false,
        },
        {
          cle: 'representeeDepuis',
          entete: 'Représentée depuis',
          aide: 'Date civile (AAAA-MM-JJ ou JJ/MM/AAAA). Facultatif.',
          obligatoire: false,
        },
        {
          cle: 'representeeJusquau',
          entete: 'Représentée jusqu’au',
          aide: 'Date civile, après « Représentée depuis ». Facultatif.',
          obligatoire: false,
        },
        {
          cle: 'exclusivite',
          entete: 'Exclusivité',
          aide: 'oui ou non. Vide : non.',
          obligatoire: false,
        },
        {
          cle: 'visibleSurLeSite',
          entete: 'Visible sur le site',
          aide: 'oui ou non. Vide : non, une reprise ne publie rien (DH5).',
          obligatoire: false,
        },
      ],
    },
    {
      nature: 'oeuvres',
      colonnes: [
        { cle: 'numeroInventaire', entete: 'Numéro d’inventaire', aide: '', obligatoire: false },
        { cle: 'titre', entete: 'Titre', aide: '', obligatoire: true },
        { cle: 'artiste', entete: 'Artiste', aide: '', obligatoire: true },
        { cle: 'annee', entete: 'Année', aide: '', obligatoire: false },
        { cle: 'techniqueFr', entete: 'Technique (FR)', aide: '', obligatoire: false },
        { cle: 'techniqueEn', entete: 'Technique (EN)', aide: '', obligatoire: false },
        { cle: 'hauteur', entete: 'Hauteur (cm)', aide: '', obligatoire: false },
        { cle: 'largeur', entete: 'Largeur (cm)', aide: '', obligatoire: false },
        { cle: 'profondeur', entete: 'Profondeur (cm)', aide: '', obligatoire: false },
        { cle: 'type', entete: 'Type', aide: '', obligatoire: false },
        { cle: 'tirage', entete: 'Tirage', aide: '', obligatoire: false },
        { cle: 'statut', entete: 'Statut', aide: '', obligatoire: false },
        { cle: 'localisation', entete: 'Localisation', aide: '', obligatoire: false },
        { cle: 'prixPublic', entete: 'Prix public (EUR)', aide: '', obligatoire: false },
        { cle: 'prixArtiste', entete: 'Prix artiste (EUR)', aide: '', obligatoire: false },
        { cle: 'devise', entete: 'Devise', aide: '', obligatoire: false },
        { cle: 'regimeTva', entete: 'Régime de TVA', aide: '', obligatoire: false },
        { cle: 'droitDeSuite', entete: 'Droit de suite', aide: '', obligatoire: false },
        { cle: 'visibleSurLeSite', entete: 'Visible sur le site', aide: '', obligatoire: false },
        { cle: 'prixVisible', entete: 'Prix visible', aide: '', obligatoire: false },
        { cle: 'descriptionFr', entete: 'Description (FR)', aide: '', obligatoire: false },
        { cle: 'descriptionEn', entete: 'Description (EN)', aide: '', obligatoire: false },
        { cle: 'etiquettes', entete: 'Étiquettes', aide: '', obligatoire: false },
        { cle: 'notesInternes', entete: 'Notes internes', aide: '', obligatoire: false },
        { cle: 'images', entete: 'Images', aide: '', obligatoire: false },
      ],
    },
    {
      nature: 'contacts',
      colonnes: [
        {
          cle: 'refOrigine',
          entete: 'Référence d’origine',
          aide: 'Identifiant du contact dans l’ancien logiciel. Facultatif, mais il rend l’import rejouable sans doublon.',
          obligatoire: false,
        },
        {
          cle: 'genre',
          entete: 'Genre',
          aide: 'personne ou organisation. Vide : personne.',
          obligatoire: false,
        },
        { cle: 'prenom', entete: 'Prénom', aide: 'Facultatif.', obligatoire: false },
        {
          cle: 'nom',
          entete: 'Nom',
          aide: 'Obligatoire pour une personne ; pour une organisation, la raison sociale suffit.',
          obligatoire: false,
        },
        {
          cle: 'raisonSociale',
          entete: 'Raison sociale',
          aide: 'Pour une organisation.',
          obligatoire: false,
        },
        {
          cle: 'type',
          entete: 'Type',
          aide: 'collectionneur, institution, presse, artiste, transporteur, fournisseur. Vide : collectionneur.',
          obligatoire: false,
        },
        {
          cle: 'email',
          entete: 'E-mail',
          aide: 'Adresse principale. Sert au dédoublonnage.',
          obligatoire: false,
        },
        {
          cle: 'autresEmails',
          entete: 'Autres e-mails',
          aide: 'Séparés par | ou ;.',
          obligatoire: false,
        },
        {
          cle: 'telephones',
          entete: 'Téléphones',
          aide: 'Séparés par | ou ;.',
          obligatoire: false,
        },
        { cle: 'adresse', entete: 'Adresse', aide: 'Numéro et voie.', obligatoire: false },
        {
          cle: 'complementAdresse',
          entete: 'Complément d’adresse',
          aide: 'Facultatif.',
          obligatoire: false,
        },
        { cle: 'codePostal', entete: 'Code postal', aide: 'Facultatif.', obligatoire: false },
        { cle: 'ville', entete: 'Ville', aide: 'Facultatif.', obligatoire: false },
        {
          cle: 'pays',
          entete: 'Pays',
          aide: 'Code ISO à deux lettres (FR, BE, CH…) ou nom du pays en français. Vide : FR.',
          obligatoire: false,
        },
        { cle: 'langue', entete: 'Langue', aide: 'fr ou en. Vide : fr.', obligatoire: false },
        {
          cle: 'tva',
          entete: 'N° TVA intracommunautaire',
          aide: 'Facultatif.',
          obligatoire: false,
        },
        {
          cle: 'etiquettes',
          entete: 'Étiquettes',
          aide: 'Séparées par | ou ;.',
          obligatoire: false,
        },
        {
          cle: 'notes',
          entete: 'Notes',
          aide: 'Notes internes, jamais visibles du contact.',
          obligatoire: false,
        },
        {
          cle: 'consentement',
          entete: 'Consentement newsletter',
          aide: 'oui ou non. « oui » n’est retenu qu’avec sa date.',
          obligatoire: false,
        },
        {
          cle: 'dateConsentement',
          entete: 'Date du consentement',
          aide: 'AAAA-MM-JJ ou JJ/MM/AAAA. Obligatoire avec « oui ».',
          obligatoire: false,
        },
        {
          cle: 'preuveConsentement',
          entete: 'Preuve du consentement',
          aide: 'Comment il a été recueilli : formulaire, carte de vernissage…',
          obligatoire: false,
        },
        {
          cle: 'retraitConsentement',
          entete: 'Désinscrit le',
          aide: 'Date de désinscription : elle s’applique toujours.',
          obligatoire: false,
        },
      ],
    },
    {
      nature: 'expositions',
      colonnes: [
        {
          cle: 'refOrigine',
          entete: 'Référence d’origine',
          aide: 'Identifiant de l’exposition dans l’ancien logiciel. Facultatif ; les accrochages peuvent la désigner par elle.',
          obligatoire: false,
        },
        {
          cle: 'type',
          entete: 'Type',
          aide: 'exposition, foire ou evenement. Vide : exposition.',
          obligatoire: false,
        },
        { cle: 'titreFr', entete: 'Titre (FR)', aide: 'Obligatoire.', obligatoire: true },
        { cle: 'titreEn', entete: 'Titre (EN)', aide: 'Facultatif.', obligatoire: false },
        { cle: 'sousTitreFr', entete: 'Sous-titre (FR)', aide: 'Facultatif.', obligatoire: false },
        {
          cle: 'debut',
          entete: 'Début',
          aide: 'AAAA-MM-JJ ou JJ/MM/AAAA. Obligatoire.',
          obligatoire: true,
        },
        {
          cle: 'fin',
          entete: 'Fin',
          aide: 'AAAA-MM-JJ ou JJ/MM/AAAA. Obligatoire.',
          obligatoire: true,
        },
        {
          cle: 'lieu',
          entete: 'Lieu',
          aide: 'Nom d’un lieu déclaré dans Réglages › Lieux. Un lieu inconnu refuse la ligne.',
          obligatoire: false,
        },
        { cle: 'stand', entete: 'Stand', aide: 'Foires seulement.', obligatoire: false },
        {
          cle: 'etat',
          entete: 'État',
          aide: 'brouillon, annoncee, en_cours ou passee. Vide : passee si la fin est passée, brouillon sinon.',
          obligatoire: false,
        },
        { cle: 'texteFr', entete: 'Texte (FR)', aide: 'Facultatif.', obligatoire: false },
        { cle: 'texteEn', entete: 'Texte (EN)', aide: 'Facultatif.', obligatoire: false },
        {
          cle: 'artistes',
          entete: 'Artistes',
          aide: 'Noms séparés par | ou ;. Un artiste inconnu est créé.',
          obligatoire: false,
        },
      ],
    },
    {
      nature: 'localisations',
      colonnes: [
        {
          cle: 'refOrigine',
          entete: 'Référence d’origine',
          aide: 'Identifiant de la ligne d’historique dans l’ancien logiciel. Facultatif : il rend la ligne reconnaissable au delta.',
          obligatoire: false,
        },
        {
          cle: 'oeuvre',
          entete: 'Œuvre',
          aide: 'Obligatoire. Référence d’origine de l’œuvre, ou son numéro d’inventaire. L’œuvre doit déjà être reprise.',
          obligatoire: true,
        },
        {
          cle: 'lieu',
          entete: 'Lieu',
          aide: 'Obligatoire. Référence d’origine du lieu, ou son nom (casse et espaces ignorés). Le lieu doit déjà être repris.',
          obligatoire: true,
        },
        {
          cle: 'date',
          entete: 'Date',
          aide: 'Obligatoire. Date civile (AAAA-MM-JJ ou JJ/MM/AAAA) à laquelle l’œuvre est arrivée dans ce lieu.',
          obligatoire: true,
        },
        {
          cle: 'note',
          entete: 'Note',
          aide: 'Facultatif, 5 000 caractères au plus.',
          obligatoire: false,
        },
      ],
    },
    {
      nature: 'provenance',
      colonnes: [
        {
          cle: 'refOrigine',
          entete: 'Référence d’origine',
          aide: 'Identifiant de l’étape dans l’ancien logiciel. Facultatif : il la rend reconnaissable au delta.',
          obligatoire: false,
        },
        {
          cle: 'oeuvre',
          entete: 'Œuvre',
          aide: 'Obligatoire. Référence d’origine de l’œuvre, ou son numéro d’inventaire. L’œuvre doit déjà être reprise : aucune n’est créée.',
          obligatoire: true,
        },
        {
          cle: 'exemplaire',
          entete: 'Exemplaire',
          aide: 'Facultatif. Numéro de l’exemplaire de l’édition (« 3/8 ») ; vide : l’œuvre entière.',
          obligatoire: false,
        },
        {
          cle: 'type',
          entete: 'Type',
          aide: 'proprietaire, exposition, publication, mention. Vide : proprietaire.',
          obligatoire: false,
        },
        {
          cle: 'libelle',
          entete: 'Libellé',
          aide: 'Obligatoire, 300 caractères. Ce qui s’imprime : « Collection particulière, Lyon ».',
          obligatoire: true,
        },
        {
          cle: 'contact',
          entete: 'Contact',
          aide: 'Facultatif. Adresse e-mail ou référence d’origine du propriétaire dans le carnet. Inconnu : l’étape garde son libellé seul (avertissement).',
          obligatoire: false,
        },
        {
          cle: 'periode',
          entete: 'Période',
          aide: 'Facultatif, texte libre de 120 caractères (« 1962-1988 », « depuis 2004 »).',
          obligatoire: false,
        },
        {
          cle: 'mode',
          entete: 'Mode d’acquisition',
          aide: 'Facultatif : achat, heritage, don, commande, atelier, inconnu.',
          obligatoire: false,
        },
        {
          cle: 'source',
          entete: 'Source',
          aide: 'Facultatif, 300 caractères : catalogue, archive, facture.',
          obligatoire: false,
        },
        {
          cle: 'public',
          entete: 'Public',
          aide: 'oui ou non. Vide : non — seules les étapes publiques sortent dans les documents.',
          obligatoire: false,
        },
        {
          cle: 'note',
          entete: 'Note',
          aide: 'Facultatif, 2 000 caractères.',
          obligatoire: false,
        },
      ],
    },
    {
      nature: 'editions',
      colonnes: [
        {
          cle: 'oeuvre',
          entete: 'Œuvre',
          aide: 'Obligatoire. Référence d’origine de l’œuvre, ou son numéro d’inventaire. L’œuvre doit déjà être reprise : aucune n’est créée.',
          obligatoire: true,
        },
        {
          cle: 'numero',
          entete: 'Numéro',
          aide: 'Obligatoire, 40 caractères. Tel qu’il s’écrit sur le cartel : « 1/5 », « EA 1/2 ».',
          obligatoire: true,
        },
        {
          cle: 'type',
          entete: 'Type',
          aide: 'numerote, epreuve_artiste, epreuve_essai, hors_commerce. Vide : numerote.',
          obligatoire: false,
        },
        {
          cle: 'statut',
          entete: 'Statut',
          aide: 'disponible, reservee, vendue, en_depot, pretee, retiree, archivee. Vide : disponible.',
          obligatoire: false,
        },
        {
          cle: 'prix',
          entete: 'Prix',
          aide: 'Facultatif.',
          obligatoire: false,
        },
        {
          cle: 'devise',
          entete: 'Devise',
          aide: 'EUR, USD, GBP, CHF. Facultatif.',
          obligatoire: false,
        },
        {
          cle: 'lieu',
          entete: 'Lieu',
          aide: 'Facultatif. Référence d’origine ou nom d’un lieu déjà repris.',
          obligatoire: false,
        },
        {
          cle: 'tirage',
          entete: 'Tirage',
          aide: 'Facultatif. Le tirage de l’œuvre, tel que repris avec les œuvres : contrôlé, jamais changé.',
          obligatoire: false,
        },
        {
          cle: 'epreuvesArtiste',
          entete: 'Épreuves d’artiste',
          aide: 'Facultatif. Nombre d’épreuves d’artiste du tirage : complète l’œuvre qui n’en prévoit aucune.',
          obligatoire: false,
        },
        {
          cle: 'epreuvesEssai',
          entete: 'Épreuves d’essai',
          aide: 'Facultatif. Nombre d’épreuves d’essai du tirage : complète l’œuvre qui n’en prévoit aucune.',
          obligatoire: false,
        },
      ],
    },
    {
      nature: 'membres',
      colonnes: [
        {
          cle: 'nom',
          entete: 'Nom',
          aide: 'Facultatif, 160 caractères : le nom qui salue la personne dans son invitation.',
          obligatoire: false,
        },
        {
          cle: 'email',
          entete: 'E-mail',
          aide: 'Obligatoire. L’adresse qui reçoit l’invitation ; un membre déjà présent est laissé tel quel.',
          obligatoire: true,
        },
        {
          cle: 'role',
          entete: 'Rôle',
          aide: 'Obligatoire : equipe, lecture, comptable. La direction n’est pas reprise : l’ouverture la pose.',
          obligatoire: true,
        },
        {
          cle: 'langue',
          entete: 'Langue',
          aide: 'fr ou en. Vide : la langue de la galerie.',
          obligatoire: false,
        },
      ],
    },
  ],
};
